// OnboardingPage.js
// Shown to new users who have no organisation yet.
// Creates the org via POST /api/organizations, then redirects to the
// org home (/) where they see the projects list. From there they choose
// when — and whether — to add a project.
//
// UPDATED Jul 25 2026: Onboarding no longer force-marches users into project
// creation. Post-submit redirects to / instead of /project/new. Users land on
// the empty projects list and click "Create your first project" when they're
// ready. This avoids burning ENQ tokens / ScraperAPI credits for users who
// signed up to evaluate rather than commit a real client immediately.
//
// UPDATED Jul 26 2026: Now uses useUser() hook directly to get Clerk user ID
// instead of relying on prop drilling from App.js. This ensures the x-user-id
// header is always present in the POST request.
//
// UPDATED Jul 27 2026: Added onOrgCreated callback to update hasOrg in parent
// before navigate('/') is called. This prevents the home route from redirecting
// back to /onboarding when the user has just created an org.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';

const TEAL = '#2a8ba8';
const DARK = '#39393a';
const LIGHT = '#f5f9fa';
const ERROR = '#c0392b';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: LIGHT,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '480px',
  },
  logo: {
    height: '48px',
    marginBottom: '32px',
    display: 'block',
  },
  step: {
    fontSize: '12px',
    fontWeight: '600',
    color: TEAL,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '8px',
  },
  heading: {
    fontSize: '24px',
    fontWeight: '700',
    color: DARK,
    margin: '0 0 8px',
  },
  subheading: {
    fontSize: '14px',
    color: '#666',
    margin: '0 0 32px',
    lineHeight: '1.5',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: DARK,
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1.5px solid #ddd',
    borderRadius: '8px',
    outline: 'none',
    fontFamily: 'inherit',
    color: DARK,
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  hint: {
    fontSize: '12px',
    color: '#999',
    marginTop: '6px',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: TEAL,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '24px',
    fontFamily: 'inherit',
    transition: 'background-color 0.2s',
  },
  buttonDisabled: {
    backgroundColor: '#aaa',
    cursor: 'not-allowed',
  },
  error: {
    backgroundColor: '#fdf0ef',
    border: `1px solid ${ERROR}`,
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '13px',
    color: ERROR,
    marginTop: '16px',
  },
};

export default function OnboardingPage({ apiBaseUrl, onOrgCreated }) {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Show loading state while Clerk is initializing
  if (!isLoaded) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Safety check — user should always be defined in protected route, but just in case
  if (!user?.id) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={styles.error}>Authentication failed. Please refresh and try again.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!orgName.trim()) return;

    console.log('🔴 handleSubmit called');
    console.log('🔴 orgName value:', orgName.trim());
    console.log('🔴 apiBaseUrl value:', apiBaseUrl);
    console.log('🔴 user.id value:', user?.id);

    setLoading(true);
    setError(null);

    try {
      console.log('🔴 About to fetch from:', `${apiBaseUrl}/organizations`);
      const res = await fetch(`${apiBaseUrl}/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id, // ← Clerk user ID from useUser hook
        },
        body: JSON.stringify({ name: orgName.trim() }),
      });
      console.log('🔴 Fetch completed, response status:', res.status);

      const data = await res.json();

      // Already has an org — that's fine, just proceed to the org home
      if (!res.ok && data.error && data.error.includes('already belongs')) {
        console.log('🔴 User already belongs to org, calling onOrgCreated and navigating');
        if (onOrgCreated) {
          onOrgCreated();
        }
        navigate('/');
        return;
      }

      if (!res.ok) {
        throw new Error(data.error || 'Failed to create organisation');
      }

      // Success — signal parent that org was created, then navigate home.
      // This updates hasOrg state so the / route doesn't redirect back to /onboarding.
      console.log('🔴 Org created successfully, calling onOrgCreated and navigating');
      if (onOrgCreated) {
        onOrgCreated();
      }

      // Land on the org home (projects list). The user chooses when
      // to add a project rather than being force-marched into ENQ discovery.
      navigate('/');
    } catch (err) {
      console.error('🔴 ERROR CAUGHT:', err);
      console.error('🔴 Error message:', err.message);
      console.error('🔴 Full error stack:', err.stack);
      setError(err.message);
      setOrgName(''); // Clear on error
    } finally {
      console.log('🔴 Finally block executing');
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading && orgName.trim()) {
      handleSubmit();
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img
          src="/PNG_White_Jetpack_Logo_300dpi.png"
          alt="GEO Jetpack"
          style={{
            ...styles.logo,
            filter: 'invert(1) sepia(1) saturate(3) hue-rotate(160deg)',
          }}
        />
        <p style={styles.step}>Get started</p>
        <h1 style={styles.heading}>Set up your workspace</h1>
        <p style={styles.subheading}>
          This is your agency or company name — the workspace all your client
          projects will live under.
        </p>
        <label style={styles.label} htmlFor="orgName">
          Agency or company name
        </label>
        <input
          id="orgName"
          type="text"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g. Bright Digital, Smith & Jones Agency"
          style={styles.input}
          onFocus={(e) => (e.target.style.borderColor = TEAL)}
          onBlur={(e) => (e.target.style.borderColor = '#ddd')}
          autoFocus
          disabled={loading}
        />
        <p style={styles.hint}>This can be changed later in Settings.</p>
        {error && <div style={styles.error}>{error}</div>}
        <button
          onClick={handleSubmit}
          disabled={loading || !orgName.trim()}
          style={{
            ...styles.button,
            ...(loading || !orgName.trim() ? styles.buttonDisabled : {}),
          }}
        >
          {loading ? 'Creating workspace…' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}
